import {
  useGetIdentity,
  useGetList,
  useListContext,
  useLocaleState,
  useTranslate,
  type Identifier,
} from "ra-core";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useNavigate } from "react-router";

import { cn } from "@/lib/utils";
import type { CrmCompany, Sale } from "../types";
import { AmountDelta, RenewalPill } from "./renewal";
import { formatEuros } from "./format";

type TableCompany = CrmCompany;

const STALE_DAYS = 90;
const CURRENT_YEAR = new Date().getFullYear();

/** One-click filters over what a sales rep checks every day. */
const useQuickFilters = () => {
  const { identity } = useGetIdentity();
  const staleSince = new Date(Date.now() - STALE_DAYS * 86_400_000)
    .toISOString()
    .slice(0, 10);
  return [
    { key: "all", filter: {} },
    { key: "mine", filter: identity ? { sales_id: identity.id } : {} },
    { key: "pending", filter: { renewal_status: "pending" } },
    { key: "down", filter: { renewal_status: "down" } },
    { key: "new", filter: { renewal_status: "new" } },
    { key: "shared", filter: { is_shared: true } },
    { key: "stale", filter: { "last_company_note_at@lt": staleSince } },
  ] as const;
};

const QUICK_KEYS = [
  "sales_id",
  "renewal_status",
  "is_shared",
  "last_company_note_at@lt",
];

export const CompanyQuickFilters = () => {
  const translate = useTranslate();
  const { filterValues, setFilters } = useListContext();
  const filters = useQuickFilters();

  const base = Object.fromEntries(
    Object.entries(filterValues ?? {}).filter(([k]) => !QUICK_KEYS.includes(k)),
  );
  const isActive = (filter: Record<string, unknown>) => {
    const active = Object.entries(filterValues ?? {}).filter(([k]) =>
      QUICK_KEYS.includes(k),
    );
    const wanted = Object.entries(filter);
    return (
      active.length === wanted.length &&
      wanted.every(([k, v]) => filterValues?.[k] === v)
    );
  };

  return (
    <div
      className="flex flex-wrap gap-2"
      role="group"
      aria-label={translate("crm.construnario.companies.filters")}
    >
      {filters.map(({ key, filter }) => (
        <QuickFilterChip
          key={key}
          label={translate(`crm.construnario.companies.quick.${key}`)}
          filter={filter}
          active={isActive(filter)}
          onSelect={() => setFilters({ ...base, ...filter }, {})}
        />
      ))}
    </div>
  );
};

const QuickFilterChip = ({
  label,
  filter,
  active,
  onSelect,
}: {
  label: string;
  filter: Record<string, unknown>;
  active: boolean;
  onSelect: () => void;
}) => {
  // A cheap count query per chip (limit 1, exact count) so the rep sees the
  // size of each bucket before clicking.
  const { total } = useGetList("companies", {
    filter,
    pagination: { page: 1, perPage: 1 },
  });
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onSelect}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors",
        active
          ? "border-primary/40 bg-accent font-medium text-accent-text"
          : "border-border bg-card text-muted-foreground hover:border-muted-foreground",
      )}
    >
      {label}
      {total != null ? (
        <span className="tabular-nums opacity-80">{total}</span>
      ) : null}
    </button>
  );
};

const SortHeader = ({
  field,
  label,
  align = "left",
}: {
  field: string;
  label: string;
  align?: "left" | "right";
}) => {
  const { sort, setSort } = useListContext();
  const active = sort?.field === field;
  const Icon = sort?.order === "ASC" ? ArrowUp : ArrowDown;
  return (
    <th
      className={cn(
        "px-3 py-2.5 text-[11.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground whitespace-nowrap",
        align === "right" ? "text-right" : "text-left",
      )}
      aria-sort={
        active ? (sort.order === "ASC" ? "ascending" : "descending") : "none"
      }
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 uppercase hover:text-foreground"
        onClick={() =>
          setSort({
            field,
            order: active && sort.order === "DESC" ? "ASC" : "DESC",
          })
        }
      >
        {label}
        {active ? <Icon className="size-3" /> : null}
      </button>
    </th>
  );
};

/** Company list as a working table (replaces Atomic's logo card grid). */
export const CompanyTable = () => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const navigate = useNavigate();
  const { data, isPending } = useListContext<TableCompany>();
  const { data: sales } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 200 },
  });
  const salesById = new Map((sales ?? []).map((s) => [s.id, s]));
  const relative = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  const lastNote = (value?: string | null) => {
    if (!value) return "—";
    const days = Math.round(
      (Date.now() - new Date(value).getTime()) / 86_400_000,
    );
    return days < 31
      ? relative.format(-days, "day")
      : new Date(value).toLocaleDateString(locale, {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
  };

  if (isPending) return null;

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      <table className="w-full min-w-[880px] border-collapse text-sm">
        <thead className="border-b border-border">
          <tr>
            <SortHeader
              field="name"
              label={translate("crm.construnario.companies.columns.company")}
            />
            <th className="px-3 py-2.5 text-left text-[11.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
              {translate("crm.construnario.companies.columns.owner")}
            </th>
            <SortHeader
              field="last_company_note_at"
              label={translate("crm.construnario.companies.columns.last_note")}
            />
            <SortHeader
              field="nb_company_notes"
              align="right"
              label={translate("crm.construnario.companies.columns.notes")}
            />
            <SortHeader
              field="contract_amount_current"
              align="right"
              label={translate("crm.construnario.companies.columns.contract", {
                year: CURRENT_YEAR,
              })}
            />
            <SortHeader
              field="renewal_status"
              label={translate("crm.construnario.companies.columns.renewal")}
            />
            <th className="px-3 py-2.5 text-left text-[11.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
              {translate("crm.construnario.companies.columns.items")}
            </th>
          </tr>
        </thead>
        <tbody>
          {data?.map((company) => {
            const recent =
              company.last_company_note_at &&
              Date.now() - new Date(company.last_company_note_at).getTime() <
                30 * 86_400_000;
            const open = () => navigate(`/companies/${company.id}/show`);
            return (
              <tr
                key={company.id}
                tabIndex={0}
                onClick={open}
                onKeyDown={(e) => e.key === "Enter" && open()}
                className="cursor-pointer border-b border-secondary last:border-0 hover:bg-secondary/70 focus-visible:bg-secondary/70"
              >
                <td className="px-3 py-2.5">
                  <div className="font-medium">{company.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {company.city}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <OwnerAvatars
                    ids={[
                      ...(company.sales_id ? [company.sales_id] : []),
                      ...(company.collaborators ?? []),
                    ]}
                    salesById={salesById}
                  />
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5">
                    {recent ? (
                      <span className="size-1.5 rounded-full bg-good" />
                    ) : null}
                    {lastNote(company.last_company_note_at)}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {company.nb_company_notes ?? 0}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                  {formatEuros(company.contract_amount_current)}
                  <AmountDelta
                    current={company.contract_amount_current}
                    previous={company.contract_amount_previous}
                  />
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <RenewalPill status={company.renewal_status} />
                  {company.renewal_status === "pending" ? (
                    <span className="ml-2 text-xs tabular-nums text-muted-foreground">
                      {CURRENT_YEAR - 1}:{" "}
                      {formatEuros(company.contract_amount_previous)}
                    </span>
                  ) : null}
                </td>
                <td className="max-w-64 px-3 py-2.5 text-[13px] text-muted-foreground">
                  {company.contract_items_current || "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const initials = (sale?: Sale) =>
  sale
    ? `${sale.first_name?.[0] ?? ""}${sale.last_name?.[0] ?? ""}`.toUpperCase() ||
      "?"
    : "?";

const OwnerAvatars = ({
  ids,
  salesById,
}: {
  ids: Identifier[];
  salesById: Map<Identifier, Sale>;
}) => (
  <span className="flex">
    {ids.map((id, index) => {
      const sale = salesById.get(id);
      const name = sale ? `${sale.first_name} ${sale.last_name}`.trim() : "";
      return (
        <span
          key={id}
          title={name}
          className={cn(
            "grid size-6 place-items-center rounded-full border-2 border-card text-[10.5px] font-semibold",
            index === 0
              ? "bg-primary text-primary-foreground"
              : "-ml-1.5 bg-neutral-tint text-foreground",
          )}
        >
          {initials(sale)}
        </span>
      );
    })}
  </span>
);
